import { useEffect, lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { GoogleOAuthProvider } from "@react-oauth/google";
import * as Sentry from "@sentry/react";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import AuthCallback from "@/components/AuthCallback";
import { api } from "@/lib/api";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import MagicVerify from "@/pages/MagicVerify";
import SetPassword from "@/pages/SetPassword";
import CreatePassword from "@/pages/CreatePassword";
import Privacy from "@/pages/Privacy";
import Terms from "@/pages/Terms";
import PortalLogin from "@/pages/portal/PortalLogin";
import PortalHome from "@/pages/portal/PortalHome";
import PortalVerify from "@/pages/portal/PortalVerify";
import PortalLayout from "@/pages/portal/PortalLayout";
import PortalRequests from "@/pages/portal/PortalRequests";
import PortalAmenities from "@/pages/portal/PortalAmenities";
import PortalEvents from "@/pages/portal/PortalEvents";
import PortalSurveys from "@/pages/portal/PortalSurveys";
import PortalDocuments from "@/pages/portal/PortalDocuments";
import PortalContacts from "@/pages/portal/PortalContacts";
import PortalNotifications from "@/pages/portal/PortalNotifications";
import PortalAccount from "@/pages/portal/PortalAccount";
import PortalActivate from "@/pages/portal/PortalActivate";
import CommunitySite from "@/pages/CommunitySite";
import CommunityLayout from "@/pages/CommunityLayout";
import PublicPage from "@/pages/dashboard/PublicPage";
import DPA from "@/pages/DPA";
import Optin from "@/pages/Optin";
import PreviewHero from "@/pages/PreviewHero";
import KitHub from "@/pages/KitHub";
import KitPrint from "@/pages/KitPrint";
import CompareIndex from "@/pages/marketing/CompareIndex";
import ComparePage from "@/pages/marketing/ComparePage";
import SolutionsIndex from "@/pages/marketing/SolutionsIndex";
import SolutionsPage from "@/pages/marketing/SolutionsPage";
import FieldServicesPage from "@/pages/marketing/FieldServicesPage";
import Resources from "@/pages/marketing/Resources";
import FaqPage from "@/pages/marketing/FaqPage";
import SecurityPage from "@/pages/marketing/SecurityPage";
import PricingPage from "@/pages/marketing/PricingPage";
import AboutPage from "@/pages/marketing/AboutPage";
import CareersPage from "@/pages/marketing/CareersPage";
import PlaybookPage from "@/pages/marketing/PlaybookPage";
import GuidePage from "@/pages/marketing/GuidePage";
import SharedDocuments from "@/pages/SharedDocuments";
import SignPage from "@/pages/SignPage";
import DashboardLayout from "@/pages/dashboard/DashboardLayout";
import Overview from "@/pages/dashboard/Overview";
import Simulator from "@/pages/dashboard/Simulator";
import Conversations from "@/pages/dashboard/Conversations";
import Documents from "@/pages/dashboard/Documents";
import Roster from "@/pages/dashboard/Roster";
import ResidentDetail from "@/pages/dashboard/ResidentDetail";
import Actions from "@/pages/dashboard/Actions";
import Security from "@/pages/dashboard/Security";
import Modules from "@/pages/dashboard/Modules";
import OperatorConsole from "@/pages/dashboard/OperatorConsole";
import Traffic from "@/pages/dashboard/Traffic";
import Advanced from "@/pages/dashboard/Advanced";
import Communities from "@/pages/dashboard/Communities";
import NumbersHub from "@/pages/dashboard/NumbersHub";
import Team from "@/pages/dashboard/Team";
import SettingsPage from "@/pages/dashboard/SettingsPage";
import Account from "@/pages/dashboard/Account";
import Reports from "@/pages/dashboard/Reports";
import VoiceDemoAdmin from "@/pages/dashboard/VoiceDemoAdmin";
import Rules from "@/pages/dashboard/Rules";
import Broadcasts from "@/pages/dashboard/Broadcasts";
import Amenities from "@/pages/dashboard/Amenities";
import Surveys from "@/pages/dashboard/Surveys";
import Signatures from "@/pages/dashboard/Signatures";
import Emergency from "@/pages/dashboard/Emergency";
import Events from "@/pages/dashboard/Events";
import Contacts from "@/pages/dashboard/Contacts";
import Feedback from "@/pages/dashboard/Feedback";
import Mail from "@/pages/dashboard/Mail";
import Websites from "@/pages/dashboard/Websites";
import ProductSignals from "@/pages/dashboard/ProductSignals";
import ActivityLog from "@/pages/dashboard/ActivityLog";
import PlatformIntelligence from "@/pages/dashboard/PlatformIntelligence";
import PortfolioHome from "@/pages/dashboard/PortfolioHome";
import PortfolioRequests from "@/pages/dashboard/PortfolioRequests";
import PortfolioCommunities from "@/pages/dashboard/PortfolioCommunities";
import PortfolioSettings from "@/pages/dashboard/PortfolioSettings";
import Roles from "@/pages/dashboard/Roles";
import PulseTabs from "@/pages/dashboard/PulseTabs";
import FeedbackHub from "@/pages/dashboard/FeedbackHub";
import ManagementCompanies from "@/pages/dashboard/ManagementCompanies";
import BillingHub from "@/pages/dashboard/BillingHub";
import PlatformRules from "@/pages/dashboard/PlatformRules";
import Industries from "@/pages/dashboard/Industries";
import Appearance from "@/pages/dashboard/Appearance";
import VoiceHealth from "@/pages/dashboard/VoiceHealth";
import BrainDiagnostic from "@/pages/dashboard/BrainDiagnostic";
import Copilot from "@/pages/dashboard/Copilot";
import CommunityProfile from "@/pages/dashboard/CommunityProfile";
import Renewals from "@/pages/dashboard/Renewals";
import FieldServicesFlow from "@/pages/FieldServicesFlow";
import FieldServices from "@/pages/dashboard/FieldServices";
import Checklists from "@/pages/dashboard/Checklists";
import JobPage from "@/pages/fs/JobPage";
import CrewOnboarding from "@/pages/fs/CrewOnboarding";
import VendorOnboarding from "@/pages/fs/VendorOnboarding";
import VendorLogin from "@/pages/fs/VendorLogin";
import VendorApply from "@/pages/fs/VendorApply";
import VendorNetwork from "@/pages/fs/VendorNetwork";
import VisitPage from "@/pages/fs/VisitPage";
import WorkPage from "@/pages/work/WorkPage";
import PublicProjects, { PublicProject } from "@/pages/fs/PublicProjects";
import VendorDirectory from "@/pages/dashboard/VendorDirectory";
import PortfolioPulse from "@/pages/dashboard/pulse/PortfolioPulse";
import MinePulse from "@/pages/dashboard/pulse/MinePulse";
import AdminPulse from "@/pages/dashboard/pulse/AdminPulse";
import CommunityPulse from "@/pages/dashboard/pulse/CommunityPulse";

import ManagerConcept from "@/pages/marketing/ManagerConcept";
const ResidentsConcept = lazy(() => import("@/pages/marketing/residents/ResidentsConcept"));
const ChecklistsMock = lazy(() => import("@/pages/mockups/checklists"));

function Protected({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="h-10 w-10 rounded-full border-2 border-redline-500 border-t-transparent animate-spin" />
      </div>
    );
  }
  if (!user) {
    const dest = location.pathname + location.search;
    if (dest.startsWith("/dashboard") && dest !== "/dashboard") {
      try { localStorage.setItem("rl_post_login", dest); } catch { /* noop */ }
    }
    return <Navigate to="/login" replace />;
  }
  // A temporary password (read out by an admin) opens the door once; the console stays closed
  // until it has been replaced with one only the teammate knows.
  if (user.must_change_password && location.pathname !== "/auth/create-password") {
    return <Navigate to="/auth/create-password" replace />;
  }
  return children;
}

// A shared marketing URL like /pricing lands on the matching section of the homepage instead of
// silently bouncing to the top of it.
function SectionRedirect({ to }) {
  return <Navigate to={`/#${to}`} replace />;
}

function AppRouter() {
  const location = useLocation();
  useEffect(() => {
    let vid = localStorage.getItem("rl_vid");
    if (!vid) { vid = `v_${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`; localStorage.setItem("rl_vid", vid); }
    api.post("/track/visit", { path: location.pathname, referrer: document.referrer || "", visitor_id: vid }).catch(() => {});
  }, [location.pathname]);
  // Land on the right section when a /pricing-style URL redirects to a hash (react-router
  // doesn't scroll to a hash on its own).
  useEffect(() => {
    const id = (location.hash || "").replace("#", "");
    if (!id || id.includes("session_id=")) return;
    const t = setTimeout(() => {
      const el = document.getElementById(id);
      if (typeof el?.scrollIntoView === "function") el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 250);
    return () => clearTimeout(t);
  }, [location.hash, location.pathname]);
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/" element={<ManagerConcept />} />
      <Route path="/classic" element={<Landing />} />
      <Route path="/manager-copilot-concept" element={<Navigate to="/" replace />} />
      <Route path="/residents-concept" element={<Suspense fallback={<div className="p-12 text-center" role="status" data-testid="residents-concept-loading">Loading design concept…</div>}><ResidentsConcept /></Suspense>} />
      <Route path="/login" element={<Login />} />
      <Route path="/portal/login" element={<PortalLogin />} />
      <Route path="/c/:slug" element={<CommunityLayout />}>
        <Route index element={<CommunitySite />} />
        <Route path="login" element={<Login />} />
        <Route path="portal" element={<PortalLayout />}>
          <Route index element={<PortalHome />} />
          <Route path="requests" element={<PortalRequests />} />
          <Route path="amenities" element={<PortalAmenities />} />
          <Route path="events" element={<PortalEvents />} />
          <Route path="surveys" element={<PortalSurveys />} />
          <Route path="documents" element={<PortalDocuments />} />
          <Route path="contacts" element={<PortalContacts />} />
          <Route path="notifications" element={<PortalNotifications />} />
          <Route path="account" element={<PortalAccount />} />
        </Route>
        <Route path="share/:token" element={<SharedDocuments />} />
        <Route path="sign/:token" element={<SignPage />} />
        <Route path="opt-in" element={<Optin />} />
      </Route>
      <Route path="/portal/verify" element={<PortalVerify />} />
      <Route path="/portal/activate" element={<PortalActivate />} />
      <Route path="/portal" element={<PortalLayout />}>
        <Route index element={<PortalHome />} />
        <Route path="requests" element={<PortalRequests />} />
        <Route path="amenities" element={<PortalAmenities />} />
        <Route path="events" element={<PortalEvents />} />
        <Route path="surveys" element={<PortalSurveys />} />
        <Route path="documents" element={<PortalDocuments />} />
        <Route path="contacts" element={<PortalContacts />} />
        <Route path="notifications" element={<PortalNotifications />} />
        <Route path="account" element={<PortalAccount />} />
      </Route>
      <Route path="/auth/magic" element={<MagicVerify />} />
      <Route path="/auth/set-password" element={<SetPassword />} />
      <Route path="/auth/create-password" element={<Protected><CreatePassword /></Protected>} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/dpa" element={<DPA />} />
      <Route path="/opt-in" element={<Optin />} />
      <Route path="/opt-in/:slug" element={<Optin />} />
      <Route path="/preview-hero" element={<PreviewHero />} />
      {/* Clickable design prototype for the Checklists module — no live data behind it. */}
      <Route path="/mockups/checklists" element={<Suspense fallback={<div className="p-12 text-center" role="status" data-testid="checklists-mock-loading">Loading prototype…</div>}><ChecklistsMock /></Suspense>} />
      <Route path="/kit" element={<KitHub />} />
      <Route path="/kit/print" element={<KitPrint />} />
      {/* /status is retired — the uptime page is no longer part of the marketing site. */}
      <Route path="/status" element={<Navigate to="/" replace />} />
      <Route path="/flow/field-services" element={<Protected><FieldServicesFlow /></Protected>} />
      <Route path="/job/:token" element={<JobPage />} />
      <Route path="/crew/:token" element={<CrewOnboarding />} />
      <Route path="/vendor" element={<VendorLogin />} />
      <Route path="/vendor/:token" element={<VendorOnboarding />} />
      <Route path="/vendor-apply" element={<VendorApply />} />
      <Route path="/vendor-apply/:token" element={<VendorApply />} />
      <Route path="/vendor-network" element={<VendorNetwork />} />
      <Route path="/visit/:token" element={<VisitPage />} />
      <Route path="/work/:token" element={<WorkPage />} />
      <Route path="/projects" element={<PublicProjects />} />
      <Route path="/projects/:ident" element={<PublicProject />} />
      <Route path="/compare" element={<CompareIndex />} />
      <Route path="/compare/:slug" element={<ComparePage />} />
      <Route path="/solutions" element={<SolutionsIndex />} />
      <Route path="/solutions/:slug" element={<SolutionsPage />} />
      <Route path="/field-services" element={<FieldServicesPage />} />
      {/* Names people will guess or type from a business card. */}
      <Route path="/vendors" element={<Navigate to="/field-services" replace />} />
      <Route path="/work-orders" element={<Navigate to="/field-services" replace />} />
      <Route path="/resources" element={<Resources />} />
      <Route path="/resources/:slug" element={<GuidePage />} />
      <Route path="/faq" element={<FaqPage />} />
      <Route path="/about" element={<AboutPage />} />
      <Route path="/careers" element={<CareersPage />} />
      <Route path="/playbook" element={<PlaybookPage />} />
      <Route path="/share/:token" element={<SharedDocuments />} />
      <Route path="/sign/:token" element={<SignPage />} />
      <Route path="/dashboard" element={<Protected><DashboardLayout /></Protected>}>
        <Route index element={<Overview />} />
        <Route path="simulator" element={<Simulator />} />
        <Route path="conversations" element={<Conversations />} />
        <Route path="documents" element={<Documents />} />
        <Route path="activity" element={<ActivityLog />} />
        <Route path="rules" element={<Rules />} />
        <Route path="broadcasts" element={<Broadcasts />} />
        <Route path="amenities" element={<Amenities />} />
        <Route path="surveys" element={<Surveys />} />
        <Route path="signatures" element={<Signatures />} />
        <Route path="field-services" element={<FieldServices />} />
        <Route path="checklists" element={<Checklists />} />
        <Route path="find-a-vendor" element={<Navigate to="/dashboard/field-services?tab=find" replace />} />
        <Route path="vendor-directory" element={<VendorDirectory />} />
        <Route path="emergency" element={<Emergency />} />
        <Route path="events" element={<Events />} />
        <Route path="contacts" element={<Contacts />} />
        <Route path="public-page" element={<PublicPage />} />
        <Route path="intelligence" element={<PlatformIntelligence />} />
        <Route path="feedback" element={<FeedbackHub />} />
        <Route path="mail" element={<Mail />} />
        <Route path="websites" element={<Websites />} />
        <Route path="product-signals" element={<Navigate to="/dashboard/feedback?tab=requests" replace />} />
        <Route path="portfolio" element={<PortfolioHome />} />
        <Route path="portfolio-pulse" element={<PulseTabs pulse={<PortfolioPulse />} />} />
        <Route path="mine/pulse" element={<MinePulse />} />
        <Route path="admin-pulse" element={<PulseTabs pulse={<AdminPulse />} />} />
        <Route path="pulse" element={<CommunityPulse />} />
        <Route path="portfolio-requests" element={<PortfolioRequests />} />
        {/* iter198m: legacy /portfolio-inbox URL kept as a redirect so any bookmarked
            links land on the new page. The old "Portfolio Inbox" was renamed +
            restricted to requests only — the confusing conversations tab is gone. */}
        <Route path="portfolio-inbox" element={<Navigate to="/dashboard/portfolio-requests" replace />} />
        <Route path="portfolio-communities" element={<PortfolioCommunities />} />
        <Route path="portfolio-settings" element={<PortfolioSettings />} />
        <Route path="roles" element={<Roles />} />
        <Route path="management-companies" element={<ManagementCompanies />} />
        <Route path="roster" element={<Roster />} />
        <Route path="roster/:personId" element={<ResidentDetail />} />
        <Route path="actions" element={<Actions />} />
        <Route path="security" element={<Security />} />
        <Route path="modules" element={<Modules />} />
        <Route path="operator" element={<OperatorConsole />} />
        <Route path="traffic" element={<Traffic />} />
        <Route path="usage" element={<Navigate to="/dashboard/billing?tab=usage" replace />} />
        <Route path="plans" element={<Navigate to="/dashboard/billing?tab=plans" replace />} />
        <Route path="billing" element={<BillingHub />} />
        <Route path="platform-rules" element={<PlatformRules />} />
        <Route path="industries" element={<Industries />} />
        <Route path="appearance" element={<Appearance />} />
        <Route path="voice-health" element={<VoiceHealth />} />
        <Route path="vendor-roles" element={<Navigate to="/dashboard/vendor-directory?tab=settings" replace />} />
        <Route path="brain-diagnostic" element={<BrainDiagnostic />} />
        <Route path="copilot" element={<Copilot />} />
        <Route path="community" element={<CommunityProfile />} />
        <Route path="renewals" element={<Renewals />} />
        <Route path="satisfaction" element={<Navigate to="/dashboard/reports?tab=satisfaction" replace />} />
        <Route path="reports" element={<Reports />} />
        <Route path="voice-demo" element={<VoiceDemoAdmin />} />
        <Route path="optins" element={<Navigate to="/dashboard/numbers?tab=optins" replace />} />
        <Route path="agent" element={<Navigate to="/dashboard/settings" replace />} />
        <Route path="advanced" element={<Advanced />} />
        <Route path="config" element={<Navigate to="/dashboard/settings?tab=service" replace />} />
        <Route path="settings" element={<SettingsPage />} />
        <Route path="account" element={<Account />} />
        <Route path="notifications" element={<Navigate to="/dashboard/account#notifications" replace />} />
        <Route path="communities" element={<Communities />} />
        <Route path="numbers" element={<NumbersHub />} />
        <Route path="team" element={<Team />} />
        {/* Unknown /dashboard/* paths keep authenticated users inside the shell (Overview)
            instead of bouncing them to the public marketing site via the global catch-all. */}
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Route>
      {/* Marketing URLs people paste into emails and decks. Before this, /pricing and friends
          hit the catch-all and silently dumped the visitor at the top of the homepage. */}
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="/plans" element={<Navigate to="/pricing" replace />} />
      <Route path="/features" element={<SectionRedirect to="copilot" />} />
      <Route path="/product" element={<SectionRedirect to="copilot" />} />
      <Route path="/demo" element={<SectionRedirect to="contact" />} />
      <Route path="/contact" element={<SectionRedirect to="contact" />} />
      <Route path="/security" element={<SecurityPage />} />
      <Route path="/service" element={<SectionRedirect to="top" />} />
      <Route path="/results" element={<SectionRedirect to="pulse" />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

const CrashScreen = () => (
  <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-sand-50 px-6 text-center" data-testid="app-crash">
    <h1 className="font-heading text-2xl font-bold text-ink">Ask Redline hit a snag on this page</h1>
    <p className="max-w-md text-sm text-ink/60">
      We have been told about it. Reload, or head back to the home page — nothing you saved is affected.
    </p>
    <div className="flex gap-2">
      <button onClick={() => window.location.reload()} data-testid="app-crash-reload"
        className="rounded-full bg-redline-500 px-5 py-2.5 font-semibold text-white hover:bg-redline-600">Reload</button>
      <a href="/" data-testid="app-crash-home"
        className="rounded-full border border-sand-300 bg-white px-5 py-2.5 font-semibold text-ink hover:border-sand-400">Home</a>
    </div>
  </div>
);

export default function App() {
  return (
    <Sentry.ErrorBoundary fallback={<CrashScreen />}>
      <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID}>
        <AuthProvider>
          <BrowserRouter>
            <AppRouter />
            <Toaster position="top-right" richColors />
          </BrowserRouter>
        </AuthProvider>
      </GoogleOAuthProvider>
    </Sentry.ErrorBoundary>
  );
}
